import { useCallback, useMemo } from "react";
import { AppId, FCX, SelectSingle, usePlatformContext } from "@datassential/platform-ui-lib";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import useCategoryProfileConsumer, {
    EMPTY_CATEGORY_PROFILE_CONSUMER,
} from "@apiHooks/useCategoryProfileConsumer";
import {
    ConsumerData,
    CONSUMER_METRICS,
    Metric,
} from "@models/ConsumerData";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { ID } from "@models";
import { requestTrialByFeature } from "@actions/page";
import CategoryProfileSectionTitle from "@pages/CategoryProfile/components/CategoryProfileSectionTitle/CategoryProfileSectionTitle";
import CategoryProfileCard from "@pages/CategoryProfile/components/CategoryProfileCard/CategoryProfileCard";
import AccessLink from "@components/AccessLink/AccessLink";
import { CategoryProfileSectionComponentProps } from "@pages/CategoryProfile/CategoryProfile.constants";
import ConsumerTrend from "./components/ConsumerTrend/ConsumerTrend";
import ConsumerAffinityIndex from "./components/ConsumerAffinityIndex/ConsumerAffinityIndex";
import {
    DEFAULT_CONSUMER_METRIC,
    CONSUMER_METRIC_LABELS,
    buildMetricStorageKey,
} from "./Consumer.constants";
import "./Consumer.styles.scss";
import http from "@core/fetch";
import apiConfig from "@apiConfig";

export const useConsumerSectionData = (categoryId: ID) => {
    const { data, isLoading } = useCategoryProfileConsumer(categoryId);
    return { hasData: data.hasCpCategories, isLoading, data };
};

const CategoryProfileConsumer: FCX<CategoryProfileSectionComponentProps> = ({ title, categoryName, data }) => {
    const consumer = (data as ConsumerData | undefined) ?? EMPTY_CATEGORY_PROFILE_CONSUMER;
    const { id: categoryId } = useParams<{ id: string }>();

    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();

    const [metric, setMetric] = useLocalStorageState<Metric>(
        buildMetricStorageKey(categoryId),
        DEFAULT_CONSUMER_METRIC,
        (raw) => {
            const value = Number(raw) as Metric;
            return CONSUMER_METRICS.includes(value) ? value : DEFAULT_CONSUMER_METRIC;
        },
    );

    const metricOptions = useMemo(
        () => CONSUMER_METRICS
            .filter(item => consumer.byMetric[item].topKeywords.length > 0)
            .map(item => ({ id: item, title: CONSUMER_METRIC_LABELS[item] })),
        [consumer],
    );

    const activeMetric = useMemo(
        () => (metricOptions.some(option => option.id === metric)
            ? metric
            : metricOptions[0]?.id ?? DEFAULT_CONSUMER_METRIC),
        [metricOptions, metric],
    );

    const metricData = consumer.byMetric[activeMetric];
    const metricLabel = CONSUMER_METRIC_LABELS[activeMetric];

    const consumerPreferences = useMemo(
        () => products[AppId.ConsumerPreferences],
        [products],
    );

    const requestTrial = useCallback(() => {
        toast.info("Request has been sent");
        dispatch(requestTrialByFeature("flavor"));
    }, [dispatch]);

    const handleDownload = useCallback(
        () => http.downloadFileXHRFromUrl(
            'GET',
            `${apiConfig['get category profile consumer report']}?categoryProfileId=${categoryId}&metric=${activeMetric}`,
        ),
        [categoryId, activeMetric]
    );

    return (
        <div className="CategoryProfile__section-content CategoryProfileConsumer">
            <CategoryProfileSectionTitle
                title={title}
                onDownload={handleDownload}
            />

            <div className="CategoryProfileConsumer__header">
                <span className="CategoryProfileConsumer__header-label">Consumer Perception</span>
                <SelectSingle
                    className="CategoryProfileConsumer__header-select"
                    value={activeMetric}
                    setValue={setMetric}
                    options={metricOptions}
                    label="Show"
                    dropdownProps={{
                        place: 'bottom-start',
                    }}
                    fieldProps={{
                        style: { minWidth: 230 },
                    }}
                    testId="selectCategoryProfileConsumerShow"
                />
            </div>

            <CategoryProfileCard>
                <ConsumerTrend
                    topKeywords={metricData.topKeywords}
                    topKeywordsByChange={metricData.topKeywordsByChange}
                    metricLabel={metricLabel}
                    categoryName={categoryName}
                />

                <div className="CategoryProfile__separator" />

                <ConsumerAffinityIndex
                    categoryId={categoryId}
                    metricLabel={metricLabel}
                    categoryName={categoryName}
                    demographics={metricData.demographics}
                />
            </CategoryProfileCard>

            <AccessLink
                url={consumerPreferences?.url || null}
                text="More Consumer Preferences"
                altText="Upgrade your subscription to explore consumer preferences."
                hasAccess={!consumerPreferences?.isDisabled}
                onContactUsClick={requestTrial}
                testId="linkCategoryProfileMoreConsumerPreferences"
            />
        </div>
    );
};

export default CategoryProfileConsumer;
